import type { RaceReplay } from '../highScores/types.ts';
import type { MapDocument } from '../map/types.ts';
import {
  activeCompetitor,
  commitActiveWithFeel,
  createMatch,
  finishActiveTurn,
  officialTimeMs,
} from '../match/match.ts';
import { sampleMovePath } from '../path.ts';
import type { RaceSplit } from '../race.ts';
import { hashSeed, mulberry32 } from '../rng.ts';
import type { TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { courseModel, prepareCourse, raceProgress, tailAt } from './course.ts';
import { decideMove } from './expert.ts';
import { BOT_LEVEL_KNOBS, type BotLevelKnobs } from './levels.ts';
import type { LiveBotLevel } from './seats.ts';
import { chooseStart, type BotStart } from './start.ts';

export type BotCrash = {
  /** 1-based turn the bounce happened on. */
  turn: number;
  position: Vec2;
  /** Speed at the wall before the bounce, px/turn. */
  impact: number;
  /** Shore crossing angle. 0 is a graze, 90 is head-on. */
  crossingDeg: number;
};

/** One turn of a bot race, for the workshop's playtest log. Distances in px, speeds px/turn. */
export type BotTurnLog = {
  turn: number;
  /** Boat before the move. */
  from: Vec2;
  speed: number;
  /** Final jobs cleared before the move. */
  progress: number;
  destination: Vec2;
  /** How far aim error moved the click from the one it meant. */
  aimOffset: number;
  /** Turns lost by slipping to a worse clean click. */
  slipTurns: number;
  /** No clean click survived the exact check; the least-bad one was taken. */
  fallback: boolean;
  crash: BotCrash | null;
};

export type BotRaceResult = {
  finished: boolean;
  /** Why the run stopped short: no legal start, no legal click, or the turn budget. */
  stopped: 'finished' | 'no-start' | 'no-move' | 'max-turns';
  turns: number;
  /** Official boat-clock ms, as a live finish would record it. Null when unfinished. */
  timeMs: number | null;
  splits: readonly RaceSplit[];
  crashes: BotCrash[];
  /** Turns that cleared no job and got less than `STUCK_GAIN_PX` closer to the next gate. */
  stuckTurns: number;
  /** Turns where no searched click survived the exact check. */
  fallbackTurns: number;
  /** Wall-clock ms spent deciding, per turn. */
  thinkMs: number[];
  /** Final jobs cleared; equals laps × jobs per lap on a finish. */
  progress: number;
  replay: RaceReplay;
  /** Boat after every turn, starting with the planted boat. */
  boats: BoatState[];
  log: BotTurnLog[];
};

export type RunBotRaceOptions = {
  /** Stable id for seeding. Defaults to the map name. */
  mapId?: string;
  seat?: number;
  maxTurns?: number;
  start?: BotStart;
  /** Speed at the start, px/turn. Live races start at rest unless Starting speed is set. */
  startSpeed?: number;
  /** Overrides the level's search knobs, for tuning sweeps. */
  knobs?: BotLevelKnobs;
  /** After every turn: jobs cleared so far, and the jobs the whole race needs. */
  onTurn?: (progress: number, total: number) => void;
};

export const DEFAULT_BOT_MAX_TURNS = 200;
/** 1 m: a turn that closes less than this on the next gate (and clears nothing) is stuck. */
const STUCK_GAIN_PX = 10;

/** Per-turn random stream, so one turn's rolls never shift another's. */
export function botTurnRng(seed: number, mapId: string, seat: number, turn: number) {
  return mulberry32(hashSeed(seed, mapId, seat, turn));
}

/**
 * Sails one bot through a whole race headlessly. Commits go through the same one-seat match
 * (`commitActiveWithFeel` / `finishActiveTurn`) live play uses, so turns, splits, and the
 * boat-clock are exactly what a human finishing on those clicks would get.
 */
export function runBotRace(
  map: MapDocument,
  feel: TurnFeel,
  level: LiveBotLevel,
  seed: number,
  options: RunBotRaceOptions = {},
): BotRaceResult {
  const mapId = options.mapId ?? map.name;
  const seat = options.seat ?? 0;
  const course = prepareCourse(map);
  const model = courseModel(course, feel);
  const knobs = options.knobs ?? BOT_LEVEL_KNOBS[level];
  const maxTurns = options.maxTurns ?? DEFAULT_BOT_MAX_TURNS;
  const start = options.start ?? chooseStart(course, { feel });
  const crashes: BotCrash[] = [];
  const thinkMs: number[] = [];
  let fallbackTurns = 0;

  if (!start) {
    return {
      finished: false,
      stopped: 'no-start',
      turns: 0,
      timeMs: null,
      splits: [],
      crashes,
      stuckTurns: 0,
      fallbackTurns,
      thinkMs,
      progress: 0,
      replay: { start: { position: { x: 0, y: 0 }, heading: 0 }, moves: [] },
      boats: [],
      log: [],
    };
  }

  let match = createMatch([
    { position: start.position, heading: start.heading, speed: options.startSpeed ?? 0 },
  ]);
  const replay: RaceReplay = {
    start: { position: { ...start.position }, heading: start.heading },
    moves: [],
  };
  const boats: BoatState[] = [activeCompetitor(match).boat];
  const log: BotTurnLog[] = [];
  let stuckTurns = 0;
  let plan: Vec2[] = [];
  let stopped: BotRaceResult['stopped'] = 'max-turns';

  for (let turn = 1; turn <= maxTurns; turn += 1) {
    const seatState = activeCompetitor(match);

    if (seatState.race.complete) {
      stopped = 'finished';
      break;
    }

    const began = performance.now();
    const decision = decideMove(course, feel, seatState.boat, seatState.race, {
      knobs,
      warm: plan,
      rng: botTurnRng(seed, mapId, seat, turn),
    });
    thinkMs.push(performance.now() - began);
    plan = decision.plan;
    fallbackTurns += decision.fallback ? 1 : 0;
    const destination = decision.destination;
    const committed = destination
      ? commitActiveWithFeel(match, destination, map, feel)
      : null;

    if (!destination || !committed) {
      stopped = 'no-move';
      break;
    }

    let crash: BotCrash | null = null;

    if (committed.commit.clipped) {
      const impactPath = committed.commit.impactPath;
      crash = {
        turn,
        position:
          (impactPath ? sampleMovePath(impactPath).at(-1) : null) ??
          committed.commit.boat.position,
        impact: committed.commit.crash?.impact ?? seatState.boat.speed,
        crossingDeg: committed.commit.crash?.crossingDeg ?? 90,
      };
      crashes.push(crash);
    }

    const jobsBefore = raceProgress(course, seatState.race);
    log.push({
      turn,
      from: { ...seatState.boat.position },
      speed: seatState.boat.speed,
      progress: jobsBefore,
      destination: { ...destination },
      aimOffset: Math.hypot(destination.x - decision.intended.x, destination.y - decision.intended.y),
      slipTurns: decision.slipTurns,
      fallback: decision.fallback,
      crash,
    });
    const gapBefore = tailAt(model, seatState.race, seatState.boat.position).dist;
    replay.moves.push({ destination: { ...destination }, radius: feel.radius });
    match = finishActiveTurn(committed.match);

    const after = activeCompetitor(match);
    boats.push(after.boat);
    options.onTurn?.(raceProgress(course, after.race), course.laps * course.jobsPerLap);

    if (
      !after.race.complete &&
      raceProgress(course, after.race) === jobsBefore &&
      gapBefore - tailAt(model, after.race, after.boat.position).dist < STUCK_GAIN_PX
    ) {
      stuckTurns += 1;
    }

    if (after.race.complete) {
      stopped = 'finished';
      break;
    }
  }

  const final = activeCompetitor(match);

  return {
    finished: final.race.complete,
    stopped,
    turns: final.moveSpeeds.length,
    timeMs: officialTimeMs(final),
    splits: final.splits,
    crashes,
    stuckTurns,
    fallbackTurns,
    thinkMs,
    progress: raceProgress(course, final.race),
    replay,
    boats,
    log,
  };
}